import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    let isBundleUpdate = false;
    let mainItemProps = null;
    let childUpdates = null;
    let itemKey = null;

    try {
      const [cartRes, bundleDataRes] = await Promise.all([
        fetch(window.Shopify.routes.root + 'cart.js'),
        fetch(window.Shopify.routes.root + '?section_id=cart-bundle-data')
      ]);
      const cartData = await cartRes.json();
      
      let bundleData = null;
      if (bundleDataRes.ok) {
        try {
          const html = await bundleDataRes.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          bundleData = JSON.parse(doc.body.textContent.trim());
        } catch(e) {}
      }

      if (bundleData && bundleData.items) {
        cartData.items.forEach(item => {
          const bInfo = bundleData.items.find(b => b.key === item.key);
          if (bInfo && bInfo.properties) {
            item.properties = { ...(item.properties || {}), ...bInfo.properties };
          }
        });
      }

      // Find the correct item key from the DOM row
      let targetKey = null;
      const row = Array.from(this.refs.cartItemRows || []).find(r => {
        const qs = r.querySelector(`[data-cart-line="${line}"]`);
        const removeBtn = r.querySelector(`[on\\:click="/onLineItemRemove/${line}"]`);
        return qs != null || removeBtn != null;
      });
      if (row && row.dataset.key) {
        targetKey = row.dataset.key;
      } else {
        // Fallback if not found in DOM
        targetKey = cartData.items[line - 1]?.key;
      }

      const itemToUpdate = cartData.items.find(i => i.key === targetKey);

      if (itemToUpdate && itemToUpdate.properties && itemToUpdate.properties._components) {
        isBundleUpdate = true;
        itemKey = itemToUpdate.key;
        const bundleId = itemToUpdate.properties._bundle_id;

        if (quantity === 0) {
          childUpdates = {};
          childUpdates[itemKey] = 0;
          cartData.items.forEach(rawItem => {
            if (rawItem.properties && rawItem.properties._is_component === 'true' &&
                (rawItem.properties._parent_key === itemKey || (bundleId && rawItem.properties._parent_bundle_id === bundleId))) {
              childUpdates[rawItem.key] = 0;
            }
          });
        } else if (quantity !== itemToUpdate.quantity) {
          let oldQty = itemToUpdate.quantity || 1;
          let newProps = { ...itemToUpdate.properties };
          for (let k in newProps) {
            if (!k.startsWith('_')) delete newProps[k];
          }
          let currentTotal = 0;
          let compsConfig = [];
          try {
            let parsedComps = JSON.parse(newProps._components || '[]');
            parsedComps.forEach(comp => {
              let scaledQty = Math.round((comp.quantity / oldQty) * quantity);
              if (scaledQty > 0) {
                compsConfig.push({ id: comp.id, quantity: scaledQty });
                currentTotal += scaledQty;
              }
            });
          } catch(e) {}

          Object.entries(itemToUpdate.properties || {}).forEach(([k, v]) => {
            if (!k.startsWith('_')) {
              let oldV = parseInt(v, 10);
              if (!isNaN(oldV)) {
                let newV = Math.round((oldV / oldQty) * quantity);
                if (newV > 0) newProps[k] = String(newV);
              }
            }
          });
          newProps._components = JSON.stringify(compsConfig);
          newProps._bundle_total_qty = String(currentTotal);
          newProps._bundle_item_qty = String(quantity);
          if (newProps._bundle_max_qty) {
            let oldMax = parseInt(newProps._bundle_max_qty, 10);
            newProps._bundle_max_qty = String(Math.round((oldMax / oldQty) * quantity));
          }
          mainItemProps = newProps;

          childUpdates = {};
          cartData.items.forEach(rawItem => {
            if (rawItem.properties && rawItem.properties._is_component === 'true' &&
                (rawItem.properties._parent_key === itemKey || (bundleId && rawItem.properties._parent_bundle_id === bundleId))) {
              const compConfig = compsConfig.find(c => c.id === rawItem.variant_id || c.id === rawItem.product_id);
              let newCompQty = compConfig ? compConfig.quantity : Math.round((rawItem.quantity / oldQty) * quantity);
              childUpdates[rawItem.key] = newCompQty;
            }
          });
        }
      }
    } catch(err) {
      console.error('Bundle scale error:', err);
    }

    let finalRequestPromise;

    if (isBundleUpdate) {
      if (quantity === 0 && childUpdates) {
        // ONE request for deletion
        finalRequestPromise = fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
          body: JSON.stringify({
            updates: childUpdates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          })
        }));
      } else if (quantity > 0) {
        // TWO concurrent requests: 1 for change, 1 for update. The update gets the sections HTML
        let promises = [];
        if (mainItemProps) {
          promises.push(fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              id: itemKey,
              quantity: quantity,
              properties: mainItemProps
            })
          })));
        }
        if (childUpdates && Object.keys(childUpdates).length > 0) {
          promises.push(fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
            body: JSON.stringify({
              updates: childUpdates,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            })
          })));
        } else {
          // If no child updates, just request sections via change
          promises.push(fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              id: itemKey,
              quantity: quantity,
              properties: mainItemProps,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            })
          })));
        }
        
        finalRequestPromise = Promise.all(promises).then(responses => {
          return responses[responses.length - 1]; // The last one has sections data
        });
      }
    } else {
      // Normal single item logic
      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
      finalRequestPromise = fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    }

    finalRequestPromise
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
